/**
 * zip.ts — FILES-BULK-01 (Seth 2026-09-28: download all of a job's files at once).
 *
 * A small "stored" zip writer (no compression — photos and PDFs are already
 * compressed, so squeezing them again only costs time). Files come straight
 * from the private bucket with the signed-in user's own access; nothing is
 * sent anywhere else. Folder names inside the zip are the document types.
 */
import { supabase } from "@/lib/supabase";
import { BUCKET, type PegasusFile } from "./filesCore";

let CRC_TABLE: Uint32Array | null = null;
function crc32(data: Uint8Array): number {
  if (!CRC_TABLE) {
    CRC_TABLE = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      CRC_TABLE[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function dosTime(d: Date): { time: number; date: number } {
  const year = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

export interface ZipEntry { name: string; data: Uint8Array; date?: Date }

/** Entries → one .zip Blob. */
export function zipBlob(entries: ZipEntry[]): Blob {
  const enc = new TextEncoder();
  const parts: BlobPart[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const e of entries) {
    const name = enc.encode(e.name);
    const crc = crc32(e.data);
    const { time, date } = dosTime(e.date ?? new Date());
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);        // version needed
    lv.setUint16(6, 0x0800, true);    // UTF-8 names
    lv.setUint16(8, 0, true);         // stored
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, e.data.length, true);
    lv.setUint32(22, e.data.length, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);
    parts.push(local, e.data as unknown as BlobPart);

    const cd = new Uint8Array(46 + name.length);
    const cv = new DataView(cd.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);        // made by
    cv.setUint16(6, 20, true);        // needed
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, e.data.length, true);
    cv.setUint32(24, e.data.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint16(30, 0, true);
    cv.setUint16(32, 0, true);
    cv.setUint16(34, 0, true);
    cv.setUint16(36, 0, true);
    cv.setUint32(38, 0, true);
    cv.setUint32(42, offset, true);
    cd.set(name, 46);
    central.push(cd);
    offset += local.length + e.data.length;
  }
  const cdSize = central.reduce((s, c) => s + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true);
  return new Blob([...parts, ...(central as unknown as BlobPart[]), end], { type: "application/zip" });
}

/** A name that's safe on Windows and a Mac, and not already used in this zip. */
function uniqueName(want: string, used: Set<string>): string {
  const clean = want.replace(/[\\:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim() || "file";
  const dot = clean.lastIndexOf(".");
  const stem = dot > 0 ? clean.slice(0, dot) : clean;
  const ext = dot > 0 ? clean.slice(dot) : "";
  let name = clean;
  for (let n = 2; used.has(name.toLowerCase()); n++) name = `${stem} (${n})${ext}`;
  used.add(name.toLowerCase());
  return name;
}

/** Download these files as one zip. `nameOf` picks each file's path inside the zip ("Photos/Front.jpg").
 *  Files that won't download are skipped and named in the result. */
export async function downloadZip(
  files: PegasusFile[],
  zipName: string,
  nameOf: (f: PegasusFile) => string = (f) => f.filename,
  onProgress?: (done: number, total: number) => void,
): Promise<{ added: number; skipped: string[] }> {
  const used = new Set<string>();
  const entries: ZipEntry[] = [];
  const skipped: string[] = [];
  let done = 0;
  // Four at a time — quick on the office line without flooding it.
  for (let i = 0; i < files.length; i += 4) {
    const batch = files.slice(i, i + 4);
    const got = await Promise.all(batch.map(async (f) => {
      const { data, error } = await supabase.storage.from(BUCKET).download(f.storage_path);
      done += 1;
      onProgress?.(done, files.length);
      if (error || !data) { skipped.push(f.filename); return null; }
      return { f, data: new Uint8Array(await data.arrayBuffer()) };
    }));
    for (const g of got) {
      if (!g) continue;
      entries.push({ name: uniqueName(nameOf(g.f), used), data: g.data, date: new Date(g.f.created_at) });
    }
  }
  if (entries.length === 0) throw new Error("None of the files would download.");
  const blob = zipBlob(entries);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = zipName.endsWith(".zip") ? zipName : `${zipName}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return { added: entries.length, skipped };
}

/** "123 Elm St, Edina" → "123 Elm St Edina" (a zip's own name). */
export const zipTitle = (s: string | null | undefined) => (s ?? "Job").replace(/[\\/:*?"<>|,]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80) || "Job";
